import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ReactFlow,
  Background,
  Controls,
  BaseEdge,
  EdgeLabelRenderer,
  Handle,
  Position,
  getBezierPath,
  MarkerType,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  api,
  type CustomerJourneyMapEdge,
  type CustomerJourneyMapNode,
  type CustomerJourneySourceMode,
} from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  type JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, formatPLN } from "@/lib/utils";

type MapNodeData = {
  payload: CustomerJourneyMapNode;
  selected: boolean;
  dimmed: boolean;
};

type MapEdgeData = {
  payload: CustomerJourneyMapEdge;
  highlighted: boolean;
  dimmed: boolean;
};

const LAYER_X: Record<string, number> = {
  source: 0,
  landing: 280,
  mid: 560,
  end: 840,
};

/** Odstęp pionowy między środkami węzłów w kolumnie. */
const NODE_ROW_PX = 72;
const CANVAS_PAD_PX = 96;
const CANVAS_MIN_PX = 640;
const CANVAS_MAX_PX = 1400;

const LAYER_CHROME: Record<string, string> = {
  source: "border-sky-500 bg-sky-50 text-sky-950",
  landing: "border-violet-500 bg-violet-50 text-violet-950",
  mid: "border-amber-500 bg-amber-50 text-amber-950",
  end: "border-emerald-600 bg-emerald-50 text-emerald-950",
};

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

function JourneyNodeView({ data }: NodeProps<Node<MapNodeData>>) {
  const n = data.payload;
  return (
    <div
      className={cn(
        "w-[150px] rounded border px-2 py-1.5 shadow-sm transition",
        LAYER_CHROME[n.layer] || "border-border bg-card",
        data.selected && "ring-2 ring-brand ring-offset-1",
        data.dimmed && "opacity-25",
      )}
      title={`${n.label}\n${n.sessions} sesji · CVR ${pct(n.cvr)}`}
    >
      <Handle type="target" position={Position.Left} className="!h-1.5 !w-1.5 !bg-slate-400" />
      <div className="truncate text-[11px] font-semibold leading-tight">{n.label}</div>
      <div className="mt-0.5 font-mono text-[10px] opacity-80">
        {n.sessions} · {pct(n.share)}
      </div>
      <Handle type="source" position={Position.Right} className="!h-1.5 !w-1.5 !bg-slate-400" />
    </div>
  );
}

function JourneyEdgeView(props: EdgeProps<Edge<MapEdgeData>>) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    data,
  } = props;
  const d = data!;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const n = d.payload.sessions;
  const stroke = d.highlighted ? "#0f766e" : d.dimmed ? "#cbd5e1" : "#64748b";
  const width = Math.max(1.5, Math.min(8, 1.5 + Math.log10(Math.max(n, 1)) * 2.5));
  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ stroke, strokeWidth: width, opacity: d.dimmed ? 0.35 : 0.9 }}
      />
      <EdgeLabelRenderer>
        <div
          className={cn(
            "nodrag nopan pointer-events-none absolute rounded bg-background/90 px-1 py-0.5 font-mono text-[9px] text-foreground shadow-sm",
            d.dimmed && "opacity-40",
          )}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          {n}
          {d.payload.share_of_source != null ? ` · ${pct(d.payload.share_of_source)}` : ""}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const nodeTypes = { journey: JourneyNodeView };
const edgeTypes = { journey: JourneyEdgeView };

/** Po załadowaniu / zmianie wysokości dociągnij cały graf w viewport. */
function FitViewOnLayout({
  nodeCount,
  canvasHeight,
}: {
  nodeCount: number;
  canvasHeight: number;
}) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (nodeCount <= 0) return;
    const id = requestAnimationFrame(() => {
      fitView({ padding: 0.14, duration: 0, minZoom: 0.25, maxZoom: 1.2 });
    });
    return () => cancelAnimationFrame(id);
  }, [nodeCount, canvasHeight, fitView]);
  return null;
}

function buildFlowElements(
  nodes: CustomerJourneyMapNode[],
  edges: CustomerJourneyMapEdge[],
  selectedId: string | null,
) {
  const byLayer: Record<string, CustomerJourneyMapNode[]> = {};
  for (const n of nodes) {
    const layer = n.layer || "other";
    if (!byLayer[layer]) byLayer[layer] = [];
    byLayer[layer].push(n);
  }
  for (const list of Object.values(byLayer)) {
    list.sort((a, b) => b.sessions - a.sessions || a.label.localeCompare(b.label, "pl"));
  }

  const related = new Set<string>();
  if (selectedId) {
    related.add(selectedId);
    for (const e of edges) {
      if (e.source === selectedId || e.target === selectedId) {
        related.add(e.source);
        related.add(e.target);
      }
    }
  }

  const flowNodes: Node<MapNodeData>[] = [];
  let maxLayerCount = 1;
  for (const [layer, list] of Object.entries(byLayer)) {
    maxLayerCount = Math.max(maxLayerCount, list.length);
    list.forEach((n, i) => {
      flowNodes.push({
        id: n.id,
        type: "journey",
        position: { x: LAYER_X[layer] ?? 0, y: i * NODE_ROW_PX },
        data: {
          payload: n,
          selected: selectedId === n.id,
          dimmed: Boolean(selectedId && !related.has(n.id)),
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
    });
  }

  const maxSessions = Math.max(1, ...edges.map((e) => e.sessions));
  const flowEdges: Edge<MapEdgeData>[] = edges.map((e) => {
    const highlighted = Boolean(
      selectedId && (e.source === selectedId || e.target === selectedId),
    );
    const dimmed = Boolean(selectedId && !highlighted);
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      type: "journey",
      data: { payload: e, highlighted, dimmed },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 14,
        height: 14,
        color: highlighted ? "#0f766e" : "#94a3b8",
      },
      style: {
        strokeWidth: 1.5 + (e.sessions / maxSessions) * 4,
      },
    };
  });

  const canvasHeight = Math.min(
    CANVAS_MAX_PX,
    Math.max(CANVAS_MIN_PX, maxLayerCount * NODE_ROW_PX + CANVAS_PAD_PX),
  );

  return { flowNodes, flowEdges, canvasHeight };
}

function HourBars({
  rows,
}: {
  rows: { hour: number; label: string; orders: number; revenue: number }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.orders));
  return (
    <div className="flex h-28 items-end gap-0.5">
      {rows.map((r) => (
        <div key={r.hour} className="flex flex-1 flex-col items-center justify-end gap-0.5">
          <div
            className="w-full rounded-t bg-emerald-600/80"
            style={{ height: `${Math.max(2, (r.orders / max) * 100)}%` }}
            title={`${r.label}: ${r.orders} · ${formatPLN(r.revenue)}`}
          />
          {r.hour % 3 === 0 ? (
            <span className="text-[8px] text-muted-foreground">{r.hour}</span>
          ) : (
            <span className="text-[8px] text-transparent">·</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function DrogaKlientki() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");
  const [sourceMode, setSourceMode] = useState<CustomerJourneySourceMode>("channel");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["customer-journey-map", period, sourceMode],
    queryFn: () =>
      api.customerJourneyMap({
        ...journeyWindowParams(period),
        source_mode: sourceMode,
      }),
    retry: false,
  });

  const data = query.data;
  const { flowNodes, flowEdges, canvasHeight } = useMemo(() => {
    if (!data?.nodes?.length) {
      return { flowNodes: [], flowEdges: [], canvasHeight: CANVAS_MIN_PX };
    }
    return buildFlowElements(data.nodes, data.edges ?? [], selectedId);
  }, [data, selectedId]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<MapNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<MapEdgeData>>([]);

  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((_: unknown, node: Node) => {
    setSelectedId((prev) => (prev === node.id ? null : node.id));
  }, []);

  const onPaneClick = useCallback(() => setSelectedId(null), []);

  const selectedNode = data?.nodes?.find((n) => n.id === selectedId) ?? null;
  const selectedEdge =
    data?.edges?.find((e) => e.id === selectedId) ??
    data?.edges?.find((e) => e.source === selectedId || e.target === selectedId) ??
    null;
  const kpi = data?.kpi;
  const topLeak = data?.leaks?.[0];
  const how = data?.how;
  const when = data?.when;

  return (
    <div>
      <PageHeader
        title="Droga klientki"
        subtitle="Skąd przychodzą, gdzie lądują, gdzie wychodzą i kiedy kupują (first-party + EasyCart)"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={period === opt.id ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="mx-1 text-muted-foreground">|</span>
        <Button
          size="sm"
          variant={sourceMode === "channel" ? "default" : "outline"}
          onClick={() => setSourceMode("channel")}
        >
          Kanał
        </Button>
        <Button
          size="sm"
          variant={sourceMode === "via" ? "default" : "outline"}
          onClick={() => setSourceMode("via")}
        >
          Via
        </Button>
        {data?.period?.label_pl && (
          <Badge variant="secondary" className="ml-1">
            {data.period.label_pl}
          </Badge>
        )}
      </div>

      {query.isLoading && (
        <p className="mt-4 text-sm text-muted-foreground">Ładowanie mapy…</p>
      )}
      {query.isError && (
        <p className="mt-4 text-sm text-destructive">
          Nie udało się pobrać mapy drogi klientki.
        </p>
      )}

      {kpi && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-medium text-muted-foreground">Sesje</CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">{kpi.sessions}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                CVR sesja → zakup
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {pct(kpi.cvr_session_to_purchase)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                CVR koszyk → zakup
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {pct(kpi.cvr_cart_to_purchase)}
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {kpi.cart_sessions} koszyków · {kpi.purchases} zakupów
              </span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Top wyciek
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm leading-snug">
              {topLeak ? (
                <>
                  <span className="font-medium">
                    {topLeak.source_label} → {topLeak.target_label}
                  </span>
                  <div className="mt-1 font-mono text-xs text-muted-foreground">
                    {topLeak.sessions} sesji · {pct(topLeak.share_of_source)} źródła
                  </div>
                </>
              ) : (
                <span className="text-muted-foreground">Brak wyraźnego wycieku</span>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_320px]">
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mapa przepływu</CardTitle>
            <p className="text-xs text-muted-foreground">
              Liczby na połączeniach = sesje. Kliknij węzeł, żeby podświetlić ścieżki.
            </p>
            {data?.layers && (
              <div className="mt-2 flex flex-wrap gap-2">
                {data.layers.map((l) => (
                  <Badge key={l.id} variant="outline">
                    {l.label}
                  </Badge>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div
              className="w-full border-t border-border/60"
              style={{ height: canvasHeight }}
            >
              {flowNodes.length > 0 ? (
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onNodeClick}
                  onPaneClick={onPaneClick}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.14, minZoom: 0.25, maxZoom: 1.2 }}
                  minZoom={0.25}
                  maxZoom={1.6}
                  proOptions={{ hideAttribution: true }}
                >
                  <FitViewOnLayout
                    nodeCount={flowNodes.length}
                    canvasHeight={canvasHeight}
                  />
                  <Background gap={18} size={1} />
                  <Controls showInteractive={false} />
                </ReactFlow>
              ) : (
                !query.isLoading && (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Brak sesji w wybranym oknie.
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Kiedy kupują</CardTitle>
              <p className="text-xs text-muted-foreground">{when?.note_pl}</p>
            </CardHeader>
            <CardContent>
              {when?.by_hour && when.orders ? (
                <>
                  <HourBars rows={when.by_hour} />
                  {when.peak_hour && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Szczyt: {when.peak_hour.label} ({when.peak_hour.orders} zam.)
                      {when.peak_dow ? ` · ${when.peak_dow.label}` : ""}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {when.by_dow?.map((d) => (
                      <Badge
                        key={d.dow}
                        variant={d.orders > 0 ? "secondary" : "outline"}
                        className="font-mono text-[10px]"
                      >
                        {d.label} {d.orders}
                      </Badge>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Brak opłaconych zamówień EasyCart w oknie.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Jak kupują</CardTitle>
              <p className="text-xs text-muted-foreground">{how?.note_pl}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {how?.products?.length ? (
                <>
                  <div className="text-sm">
                    <span className="font-semibold tabular-nums">{how.orders}</span> zam. ·{" "}
                    <span className="font-semibold tabular-nums">{formatPLN(how.revenue ?? 0)}</span>
                    {how.aov != null && (
                      <span className="text-muted-foreground"> · AOV {formatPLN(how.aov)}</span>
                    )}
                  </div>
                  <ul className="space-y-1.5">
                    {how.products.map((p) => (
                      <li
                        key={p.slug}
                        className="flex items-baseline justify-between gap-2 text-sm"
                      >
                        <span className="truncate">{p.label}</span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">
                          {p.orders} · {pct(p.share)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Brak SKU w oknie.</p>
              )}
            </CardContent>
          </Card>

          {(selectedNode || selectedEdge) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Szczegół</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {selectedNode && (
                  <>
                    <div className="font-medium">{selectedNode.label}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {selectedNode.layer} · {selectedNode.key}
                    </div>
                    <div>
                      {selectedNode.sessions} sesji · zakupy {selectedNode.purchases ?? 0} · CVR{" "}
                      {pct(selectedNode.cvr)}
                    </div>
                  </>
                )}
                {selectedEdge && !selectedNode && (
                  <div>
                    {selectedEdge.sessions} sesji na krawędzi · CVR {pct(selectedEdge.cvr)}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/sprzedaz">Sprzedaż → Lejki / Ścieżki</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {data?.gaps_pl && data.gaps_pl.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Brak pomiaru</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc space-y-1.5 pl-4 text-xs text-muted-foreground">
                  {data.gaps_pl.map((g) => (
                    <li key={g}>{g}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
